import React, { useMemo, useState } from "react";
import { ConfirmDeleteModal } from "../../components/admin/AdminDetailModals";
import Avatar from "../../ui/avatar/Avatar";
import AvatarUploadField from "../../ui/avatar/AvatarUploadField";
import GroupTag from "../../ui/tags/GroupTag";
import { AddCustomFieldDefForm, AddCustomFieldValueForm } from "../../ui/forms/CustomFieldForms";

import Button from "../../ui/buttons/Button";
import IconButton from "../../ui/buttons/IconButton";
import HoverIconButton from "../../ui/buttons/HoverIconButton";

import BaseInput from "../../ui/inputs/BaseInput/BaseInput";
import SearchInput from "../../ui/inputs/SearchInput";
import DropdownSelect from "../../ui/inputs/DropdownSelect";
import DropdownSelectPill from "../../ui/inputs/DropdownSelectPill";
import PhoneInput from "../../ui/inputs/PhoneInput";

import ChoiceChip from "../../ui/selectors/ChoiceChip";
import Pills from "../../ui/selectors/Pills";
import Toggle from "../../ui/selectors/Toggle";
import Radio from "../../ui/selectors/Radio";

import LoadingSpinner from "../../ui/spinner/LoadingSpinner";

import { MailSummaryCardRow, MailItemCardRow } from "../../ui/cards/MailCards";

import glassIcon from "../../assets/icons/glass.svg";
import { ReactComponent as TrashIcon } from "../../assets/icons/trash.svg";
import plus from "../../assets/icons/add.svg";
import rocket from "../../assets/icons/rocket.svg";
import rocketHover from "../../assets/icons/rocket-hover.svg";

const PlusIcon = (
  <img src={plus} alt="" style={{ width: 14, height: 14, display: "block" }} />
);

const GlassIcon = (
  <img
    src={glassIcon}
    alt=""
    style={{ width: 16, height: 16, display: "block" }}
  />
);

const Section = ({ title, children }) => (
  <section
    style={{
      padding: 16,
      borderRadius: 12,
      background: "#b1b1b1",
      marginBottom: 16,
    }}
  >
    <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 12 }}>
      {title}
    </div>
    {children}
  </section>
);

const Row = ({ title, children }) => (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: "260px 1fr",
      gap: 12,
      alignItems: "center",
      padding: "10px 0",
    }}
  >
    <div style={{ opacity: 0.7, fontSize: 13 }}>{title}</div>
    <div>{children}</div>
  </div>
);

const Stack = ({ children }) => (
  <div
    style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}
  >
    {children}
  </div>
);

function safeJson(v) {
  try {
    return JSON.stringify(v, null, 2);
  } catch (e) {
    return `<<no se pudo stringify: ${String(e)}>>`;
  }
}

function LoadingOverlay({ label = "Cargando…" }) {
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        pointerEvents: "auto",
        background: "rgba(0,0,0,0.15)",
      }}
      role="status"
      aria-label={label}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 10,
          alignItems: "center",
        }}
      >
        <LoadingSpinner
          variant="inline"
          size={36}
          thickness={4}
          speedMs={900}
        />
        <div style={{ fontSize: 13, opacity: 0.8 }}>{label}</div>
      </div>
    </div>
  );
}

function HoverRocketButton({
  children,
  width,
  height,
  tone = "green",
  loading = false,
  disabled = false,
  onClick,
}) {
  const [hovered, setHovered] = useState(false);

  const rocketIcon = (
    <img
      src={hovered ? rocketHover : rocket}
      alt=""
      style={{ width: 32, height: 32, display: "block" }}
    />
  );

  return (
    <Button
      variant="special"
      tone={tone}
      width={width}
      height={height}
      iconRight={rocketIcon}
      loading={loading}
      disabled={disabled}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {children}
    </Button>
  );
}

function AvatarUploadFieldDemo() {
  const [circleFile, setCircleFile] = useState(null);
  const [circleUrl, setCircleUrl] = useState(null);
  const [squareFile, setSquareFile] = useState(null);
  const [squareUrl, setSquareUrl] = useState(null);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      <Row title="Circle — sin imagen inicial (edición)">
        <AvatarUploadField
          src={circleUrl}
          isEditing={true}
          shape="circle"
          onChange={(file, url) => { setCircleFile(file); setCircleUrl(url); }}
          onDelete={() => { setCircleFile(null); setCircleUrl(null); }}
        />
        {circleFile && <span style={{ fontSize: 12, color: "var(--text-secondary)" }}>{circleFile.name}</span>}
      </Row>
      <Row title="Square — sin imagen inicial (edición)">
        <AvatarUploadField
          src={squareUrl}
          isEditing={true}
          shape="square"
          onChange={(file, url) => { setSquareFile(file); setSquareUrl(url); }}
          onDelete={() => { setSquareFile(null); setSquareUrl(null); }}
        />
        {squareFile && <span style={{ fontSize: 12, color: "var(--text-secondary)" }}>{squareFile.name}</span>}
      </Row>
      <Row title="Circle — con imagen inicial (edición)">
        <AvatarUploadField
          src="https://i.pravatar.cc/80"
          isEditing={true}
          shape="circle"
          onChange={() => {}}
          onDelete={() => {}}
        />
      </Row>
      <Row title="Square — con imagen inicial (edición)">
        <AvatarUploadField
          src="https://i.pravatar.cc/80"
          isEditing={true}
          shape="square"
          onChange={() => {}}
          onDelete={() => {}}
        />
      </Row>
      <Row title="Circle — modo vista con imagen">
        <AvatarUploadField src="https://i.pravatar.cc/80" isEditing={false} shape="circle" />
      </Row>
      <Row title="Square — modo vista sin imagen">
        <AvatarUploadField src={null} isEditing={false} shape="square" />
      </Row>
    </div>
  );
}

export default function ComponentGallery() {
  const [searchValue, setSearchValue] = useState("");
  const [baseValue, setBaseValue] = useState("");
  const [dropdownValue, setDropdownValue] = useState("");
  const [pillValue, setPillValue] = useState("op1");
  const [chipSelected, setChipSelected] = useState("chip1");
  const [phoneCountry, setPhoneCountry] = useState("AR");
  const [phoneValue, setPhoneValue] = useState("");

  const [radioValue, setRadioValue] = useState("a");
  const [toggleOn, setToggleOn] = useState(false);
  const [choiceValue, setChoiceValue] = useState("dash");
  const [pillNumber, setPillNumber] = useState("1");

  const [showOverlay, setShowOverlay] = useState(false);
  const [confirmVariant, setConfirmVariant] = useState(null); // null | "usuario" | "cuenta"

  const [showDefForm, setShowDefForm] = useState(false);
  const [defFields, setDefFields] = useState([
    { id: 1, name: "Sector", data_type: "text" },
    { id: 2, name: "Empleados", data_type: "number" },
    { id: 3, name: "Vencimiento", data_type: "date" },
    { id: 4, name: "Activo", data_type: "boolean" },
  ]);
  const [showValueForm, setShowValueForm] = useState(false);
  const [savedValues, setSavedValues] = useState([]);

  const [btnLoading, setBtnLoading] = useState(false);
  const [btnDisabled, setBtnDisabled] = useState(false);

  const dropdownOptions = useMemo(
    () => [
      {
        value: "huawei",
        label: "Huawei",
        color: { bg: "#FFF7F6", fg: "#F04438" },
      },
      { value: "fox", label: "Fox", color: { bg: "#FAFCFF", fg: "#388EFA" } },
      {
        value: "colgate",
        label: "Colgate",
        color: { bg: "#F7FFFA", fg: "#1EBD73" },
      },
      {
        value: "walmart",
        label: "Walmart",
        color: { bg: "#FFFCF5", fg: "#FDB022" },
      },
    ],
    [],
  );

  return (
    <div style={{ padding: 24, background: "#EFEDEE", minHeight: "100vh" }}>
      {showOverlay ? <LoadingOverlay label="Cargando componentes…" /> : null}

      <div style={{ fontSize: 28, fontWeight: 700, marginBottom: 16 }}>
        Component Gallery
      </div>
      <div style={{ opacity: 0.7, marginBottom: 24 }}>
        Pantalla de prueba para ver componentes con datos hardcode y logs.
      </div>

      <Section title="Dev toggles">
        <Row title="Overlay (manual)">
          <Stack>
            <Toggle checked={showOverlay} onChange={setShowOverlay} />
          </Stack>
        </Row>

        <Row title="Buttons: states">
          <Stack>
            <Toggle checked={btnLoading} onChange={setBtnLoading} />
            <div style={{ fontSize: 13, opacity: 0.8 }}>loading</div>

            <Toggle checked={btnDisabled} onChange={setBtnDisabled} />
            <div style={{ fontSize: 13, opacity: 0.8 }}>disabled</div>
          </Stack>
        </Row>
      </Section>

      <Section title="Buttons (many variants)">
        <Row title="Primary · blue · md · text">
          <Stack>
            <Button
              tone="blue"
              loading={btnLoading}
              disabled={btnDisabled}
              onClick={() => console.log("[Button] primary blue md")}
            >
              Action
            </Button>

            <Button
              tone="blue"
              iconLeft={PlusIcon}
              loading={btnLoading}
              disabled={btnDisabled}
              onClick={() =>
                console.log("[Button] primary blue md + left icon")
              }
            >
              Action
            </Button>

            <Button
              tone="blue"
              iconRight={PlusIcon}
              loading={btnLoading}
              disabled={btnDisabled}
              onClick={() =>
                console.log("[Button] primary blue md + right icon")
              }
            >
              Action
            </Button>
          </Stack>
        </Row>

        <Row title="Primary · green · md">
          <Stack>
            <Button
              tone="green"
              loading={btnLoading}
              disabled={btnDisabled}
              onClick={() => console.log("[Button] primary green md")}
            >
              Action
            </Button>

            <Button
              tone="green"
              iconLeft={PlusIcon}
              loading={btnLoading}
              disabled={btnDisabled}
              onClick={() =>
                console.log("[Button] primary green md + left icon")
              }
            >
              Action
            </Button>

            <Button
              tone="green"
              iconRight={PlusIcon}
              loading={btnLoading}
              disabled={btnDisabled}
              onClick={() =>
                console.log("[Button] primary green md + right icon")
              }
            >
              Action
            </Button>
          </Stack>
        </Row>

        <Row title="Secondary · blue · md">
          <Stack>
            <Button
              variant="secondary"
              tone="blue"
              loading={btnLoading}
              disabled={btnDisabled}
              onClick={() => console.log("[Button] secondary blue md")}
            >
              Action
            </Button>

            <Button
              variant="secondary"
              tone="blue"
              iconLeft={PlusIcon}
              loading={btnLoading}
              disabled={btnDisabled}
              onClick={() =>
                console.log("[Button] secondary blue md + left icon")
              }
            >
              Action
            </Button>
          </Stack>
        </Row>

        <Row title="Special · green · rocket hover (2 svgs)">
          <Stack>
            <HoverRocketButton
              tone="green"
              loading={btnLoading}
              disabled={btnDisabled}
              onClick={() => console.log("[Button] special send mails")}
            >
              Send Mails
            </HoverRocketButton>

            <HoverRocketButton
              tone="green"
              width={172}
              height={48}
              loading={btnLoading}
              disabled={btnDisabled}
              onClick={() => console.log("[Button] special send review")}
            >
              Send to Review
            </HoverRocketButton>
          </Stack>
        </Row>

        <Row title="Special · blue · rocket hover (2 svgs)">
          <Stack>
            <HoverRocketButton
              tone="blue"
              loading={btnLoading}
              disabled={btnDisabled}
              onClick={() => console.log("[Button] special blue")}
            >
              Special Blue
            </HoverRocketButton>
          </Stack>
        </Row>

        <Row title="Special · fixed sizes">
          <Stack>
            <HoverRocketButton
              tone="green"
              width={145}
              height={48}
              loading={btnLoading}
              disabled={btnDisabled}
              onClick={() => console.log("[Button] special 145x48")}
            >
              Send Mails
            </HoverRocketButton>

            <HoverRocketButton
              tone="green"
              width={172}
              height={48}
              loading={btnLoading}
              disabled={btnDisabled}
              onClick={() => console.log("[Button] special 172x48")}
            >
              Send to Review
            </HoverRocketButton>
          </Stack>
        </Row>

        <Row title="IconButton · danger/blue/green/neutral">
          <Stack>
            <IconButton
              tone="danger"
              size="md"
              icon={GlassIcon}
              disabled={btnDisabled}
              onClick={() => console.log("[IconButton] danger md")}
              aria-label="danger"
            />

            <IconButton
              tone="blue"
              size="md"
              icon={GlassIcon}
              disabled={btnDisabled}
              onClick={() => console.log("[IconButton] blue md")}
              aria-label="blue"
            />

            <IconButton
              tone="green"
              size="md"
              icon={GlassIcon}
              disabled={btnDisabled}
              onClick={() => console.log("[IconButton] green md")}
              aria-label="green"
            />

            <IconButton
              tone="neutral"
              size="md"
              icon={GlassIcon}
              disabled={btnDisabled}
              onClick={() => console.log("[IconButton] neutral md")}
              aria-label="neutral"
            />
          </Stack>
        </Row>
      </Section>

      <Section title="HoverIconButton">
        <Row title="Hover sobre la fila para ver el botón">
          <div style={{ display: "flex", flexDirection: "column", gap: 4, width: 280 }}>
            {["Grupo de permisos Alpha", "Grupo de permisos Beta", "Cuenta Acme"].map((label) => (
              <div
                key={label}
                className={HoverIconButton.rowClassName}
                style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 8px", borderRadius: 8, background: "var(--surface-primary)" }}
              >
                <span style={{ flex: 1, fontSize: 13, fontFamily: "DM Sans" }}>{label}</span>
                <HoverIconButton icon={<TrashIcon />} size={18} aria-label="Eliminar" onClick={() => {}} />
              </div>
            ))}
          </div>
        </Row>
      </Section>

      <Section title="Inputs">
        <Row title="BaseInput (text)">
          <div style={{ maxWidth: 420 }}>
            <BaseInput
              label="Input"
              placeholder="Placeholder"
              value={baseValue}
              onChange={(v) => {
                console.log("[BaseInput] onChange:", v);
                setBaseValue(v);
              }}
              error={baseValue === "error" ? "Mensaje de error" : ""}
            />
          </div>
        </Row>

        <Row title="SearchInput (controlled)">
          <div style={{ maxWidth: 420 }}>
            <SearchInput
              value={searchValue}
              placeholder="Buscar..."
              onChange={(v) => {
                console.log("[SearchInput] onChange:", v);
                setSearchValue(v);
              }}
              onSubmit={(v) => console.log("[SearchInput] onSubmit:", v)}
              onSearch={(v) =>
                console.log("[SearchInput] onSearch debounce:", v)
              }
            />
          </div>
        </Row>

        <Row title="PhoneInput">
          <div style={{ maxWidth: 420 }}>
            <PhoneInput
              label="Celular"
              value={phoneValue}
              onChange={(v) => setPhoneValue(v)}
              country={phoneCountry}
              onCountryChange={setPhoneCountry}
            />
          </div>
        </Row>

        <Row title="DropdownSelect">
          <div style={{ maxWidth: 420 }}>
            <DropdownSelect
              label="Cliente"
              placeholder="Seleccionar..."
              value={dropdownValue}
              options={dropdownOptions}
              onChange={(v) => {
                console.log("[DropdownSelect] onChange:", v);
                setDropdownValue(v);
              }}
              error={
                dropdownValue === "walmart" ? "Ejemplo de error (Walmart)" : ""
              }
            />
          </div>
        </Row>

        <Row title="DropdownSelectPill">
          <div style={{ maxWidth: 420 }}>
            <DropdownSelectPill
              label="Cliente"
              placeholder="Seleccionar..."
              value={dropdownValue}
              options={dropdownOptions}
              onChange={(v) => {
                console.log("[DropdownSelect] onChange:", v);
                setDropdownValue(v);
              }}
              error={
                dropdownValue === "walmart" ? "Ejemplo de error (Walmart)" : ""
              }
            />
          </div>
        </Row>
      </Section>

      <Section title="Selectors">
        <Row title="Radio">
          <Stack>
            <Radio checked={false} onChange={() => {}} />
            <Radio checked onChange={() => {}} />
            <Radio
              checked={radioValue === "a"}
              onChange={() => setRadioValue("a")}
              label="Option A"
            />
            <Radio
              checked={radioValue === "b"}
              onChange={() => setRadioValue("b")}
              label="Option B"
            />
          </Stack>
        </Row>

        <Row title="Toggle">
          <Stack>
            <Toggle checked={false} onChange={() => {}} />
            <Toggle checked onChange={() => {}} />
            <Toggle checked={toggleOn} onChange={setToggleOn} />
          </Stack>
        </Row>

        <Row title="ChoiceChip">
          <Stack>
            <ChoiceChip
              Icon={glassIcon}
              label="Chip 1"
              selected={chipSelected === "chip1"}
              onClick={() => setChipSelected("chip1")}
            />
            <ChoiceChip
              Icon={glassIcon}
              label="Chip 2"
              selected={chipSelected === "chip2"}
              onClick={() => setChipSelected("chip2")}
            />
            <ChoiceChip
              Icon={glassIcon}
              label="Dashboard"
              selected={choiceValue === "dash"}
              onClick={() => setChoiceValue("dash")}
            />
            <ChoiceChip
              Icon={glassIcon}
              label="Otro"
              selected={choiceValue === "other"}
              onClick={() => setChoiceValue("other")}
            />
            <ChoiceChip
              Icon={glassIcon}
              label="Disabled"
              disabled
              onClick={() => {}}
            />
          </Stack>
        </Row>

        <Row title="Pills">
          <Stack>
            <Pills
              options={{ op1: "Option 1", op2: "Option 2" }}
              value={pillValue}
              onChange={(v) => setPillValue(String(v))}
            />

            <Pills
              options={{ 1: "1", 2: "2", 3: "3" }}
              value={pillNumber}
              onChange={(v) => setPillNumber(String(v))}
            />
          </Stack>
        </Row>

        <Row title="Spinner">
          <Stack>
            <LoadingSpinner size={16} thickness={2} />
            <LoadingSpinner size={24} thickness={3} />
            <LoadingSpinner size={36} thickness={4} />
          </Stack>
        </Row>
      </Section>

      <Section title="Avatar">
        <Row title="Con foto — cover (personas)">
          <Stack>
            <Avatar src="https://i.pravatar.cc/32" name="Juan Pérez" size={24} />
            <Avatar src="https://i.pravatar.cc/40" name="Juan Pérez" size={32} />
            <Avatar src="https://i.pravatar.cc/48" name="Juan Pérez" size={48} />
          </Stack>
        </Row>
        <Row title="Con foto — contain (logos de cuenta)">
          <Stack>
            <Avatar src="https://i.pravatar.cc/32" name="ACME" size={24} objectFit="contain" />
            <Avatar src="https://i.pravatar.cc/40" name="ACME" size={36} objectFit="contain" />
            <Avatar src="https://i.pravatar.cc/48" name="ACME" size={48} objectFit="contain" />
          </Stack>
        </Row>
        <Row title="Sin foto — dos palabras">
          <Stack>
            <Avatar name="Juan Pérez" size={24} />
            <Avatar name="Juan Pérez" size={32} />
            <Avatar name="Juan Pérez" size={48} />
          </Stack>
        </Row>
        <Row title="Sin foto — una palabra">
          <Stack>
            <Avatar name="ACME" size={24} />
            <Avatar name="ACME" size={32} />
            <Avatar name="ACME" size={48} />
          </Stack>
        </Row>
        <Row title="Sin foto — sin nombre">
          <Stack>
            <Avatar size={24} />
            <Avatar size={32} />
            <Avatar size={48} />
          </Stack>
        </Row>
      </Section>

      <Section title="GroupTag">
        <Row title="Grupos predefinidos">
          <Stack>
            <GroupTag name="Empleado" />
            <GroupTag name="Supervisor" />
            <GroupTag name="Gerente" />
            <GroupTag name="Administrador" />
          </Stack>
        </Row>
        <Row title="Grupo desconocido (fallback)">
          <Stack>
            <GroupTag name="Otro Rol" />
            <GroupTag name="-" />
          </Stack>
        </Row>
      </Section>

      <Section title="AvatarUploadField">
        <AvatarUploadFieldDemo />
      </Section>

      <Section title="Mail cards">
        <Row title="Summary (default/late/uptodate)">
          <div style={{ display: "grid", gap: 10 }}>
            <MailSummaryCardRow
              status="default"
              statusText="80% late"
              onAction={() => console.log("action default")}
            />
            <MailSummaryCardRow
              status="late"
              statusText="80% tarde"
              onAction={() => console.log("action late")}
            />
            <MailSummaryCardRow
              status="uptodate"
              statusText="Estas al día"
              onAction={() => console.log("action uptodate")}
            />
          </div>
        </Row>

        <Row title="Item (default/late)">
          <div style={{ display: "grid", gap: 10 }}>
            <MailItemCardRow
              status="default"
              onAction={() => console.log("mail action default")}
            />
            <MailItemCardRow
              status="late"
              onAction={() => console.log("mail action late")}
            />
          </div>
        </Row>
      </Section>

      <Section title="Custom Field Forms">
        <Row title="Definir campo (nombre + tipo)">
          {showDefForm ? (
            <div style={{ maxWidth: 420 }}>
              <AddCustomFieldDefForm
                onAdd={(name, type) => {
                  const newField = { id: Date.now(), name, data_type: type };
                  setDefFields((prev) => [...prev, newField]);
                  console.log("[AddCustomFieldDefForm] added:", newField);
                  setShowDefForm(false);
                }}
                onCancel={() => setShowDefForm(false)}
              />
            </div>
          ) : (
            <Button tone="blue" onClick={() => setShowDefForm(true)}>
              Agregar dato
            </Button>
          )}
        </Row>

        <Row title="Campos definidos (mock)">
          <div style={{ fontSize: 13, opacity: 0.8 }}>
            {defFields.map((f) => (
              <span key={f.id} style={{ marginRight: 12 }}>
                <b>{f.name}</b> ({f.data_type})
              </span>
            ))}
          </div>
        </Row>

        <Row title="Cargar valor (dato + input adaptado)">
          {showValueForm ? (
            <div style={{ maxWidth: 420 }}>
              <AddCustomFieldValueForm
                fields={defFields}
                onAdd={(fieldId, value) => {
                  const field = defFields.find((f) => String(f.id) === String(fieldId));
                  const entry = { fieldId, fieldName: field?.name, value };
                  setSavedValues((prev) => [...prev, entry]);
                  console.log("[AddCustomFieldValueForm] saved:", entry);
                  setShowValueForm(false);
                }}
                onCancel={() => setShowValueForm(false)}
              />
            </div>
          ) : (
            <Button tone="blue" onClick={() => setShowValueForm(true)}>
              Cargar valor
            </Button>
          )}
        </Row>

        {savedValues.length > 0 && (
          <Row title="Valores guardados">
            <div style={{ fontSize: 13, opacity: 0.8 }}>
              {savedValues.map((v, i) => (
                <span key={i} style={{ marginRight: 12 }}>
                  <b>{v.fieldName}</b>: {String(v.value)}
                </span>
              ))}
            </div>
          </Row>
        )}
      </Section>

      <Section title="ConfirmDeleteModal">
        <Row title="Eliminar usuario">
          <Button tone="blue" onClick={() => setConfirmVariant("usuario")}>
            Abrir modal usuario
          </Button>
        </Row>
        <Row title="Eliminar cuenta">
          <Button tone="blue" onClick={() => setConfirmVariant("cuenta")}>
            Abrir modal cuenta
          </Button>
        </Row>
      </Section>

      {confirmVariant === "usuario" && (
        <ConfirmDeleteModal
          title="¿Deseas eliminar este usuario?"
          description="Eliminar este usuario definitivamente no tiene vuelta atrás."
          name="@juanmacchiavello"
          onConfirm={async () => {
            await new Promise((r) => setTimeout(r, 800));
            setConfirmVariant(null);
          }}
          onClose={() => setConfirmVariant(null)}
        />
      )}
      {confirmVariant === "cuenta" && (
        <ConfirmDeleteModal
          title="¿Deseas eliminar esta cuenta?"
          description="Eliminar esta cuenta definitivamente no tiene vuelta atrás."
          name="Apple Latinoamérica"
          onConfirm={async () => {
            await new Promise((r) => setTimeout(r, 800));
            setConfirmVariant(null);
          }}
          onClose={() => setConfirmVariant(null)}
        />
      )}

      <Section title="Debug">
        <pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>
          {safeJson({
            baseValue,
            searchValue,
            phoneValue,
            dropdownValue,
            pillValue,
            chipSelected,
            radioValue,
            toggleOn,
            choiceValue,
            pillNumber,
            showOverlay,
            btnLoading,
            btnDisabled,
          })}
        </pre>
      </Section>
    </div>
  );
}
